import React, { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Button, SelectField, TextField, FlexRow, NumberField, ISelectOption } from 'ui-toolkit';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { IInternalTransferData } from '../../../../../types/order/escrow/IInternalTransferData';
import { getFilteredLegacyInternalTransferCategories } from '../../../../../utilities/order/escrow/getFilteredLegacyInternalTransferCategories';
import { legacyInternalTransferCategoriesToOptions } from '../../../../../types/accounting/response/eInternalTransferCategory';
import { getDisabledLegacyInternalTransferCategories } from '../../../../../utilities/order/escrow/getDisabledLegacyInternalTransferCategories';
import { useSelectCurrentOrder } from '../../../../../selectors/order/selectCurrentOrder';
import { useFormikContext } from 'formik';
import { getBankAccountForCategoryAndStateAction } from '../../../../../store/order/escrow/disbursements/internalTransfers/getInternalTransferSlice';
import { useKeyToSelect } from '../../../../../store/createKeyedDataAsyncSlice';
import { RootStore } from '../../../../../store/types';
import { IEscrowBankAccountResponse } from '../../../../../types/accounting/response/IEscrowBankAccountResponse';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IParty } from '../../../../../ui-toolkit/orders/interfaces/IParty';
import { labelComparator } from '../../../../../utilities/labelComparator';
import { useAppDispatch } from '../../../../../store/store';
import { useHasFeatureFlag } from '../../../../../selectors/useHasFeatureFlag';
import { IEscrowFileCompleteData } from '../../../../../types/order/escrow/IEscrowFileCompleteData';

export interface ICreateEditInternalTransferRowProps {
  index: number;
  onDelete: (index: number) => void;
  onSend: () => void;
  fieldName: string;
  transferData: IInternalTransferData;
  parties: IParty[];
  state: string | null;
  destinationAccounts: IEscrowBankAccountResponse[];
  fetching: boolean;
}

const manualDestinationAccountStates = ['AZ', 'NV', 'TX', 'WA', null];

export const CreateEditInternalTransferRow: React.FC<ICreateEditInternalTransferRowProps> = ({
  index,
  onDelete,
  onSend,
  fieldName,
  transferData,
  parties,
  state,
  destinationAccounts,
  fetching,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedInternalTransfersEdit]);
  const { data: orderData } = useSelectCurrentOrder();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const { data: bankAccountForCategoryAndState } = useKeyToSelect(
    String(index),
    (store: RootStore) => store.order.escrow.disbursements.getBankAccountForCategoryAndState
  );
  const dispatch = useAppDispatch();
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const isManualDestinationAccountState = useMemo(
    () => manualDestinationAccountStates.includes(orderData.property.state),
    [orderData.property.state]
  );

  useEffect(() => {
    if (bankAccountForCategoryAndState && !isManualDestinationAccountState) {
      const baseName = isSpruceWithZillowEnabled
        ? bankAccountForCategoryAndState.data.legalName
        : bankAccountForCategoryAndState.data.spruceName;
      const selectedNickname = isZillowClosingEnabled
        ? bankAccountForCategoryAndState.data.nickname
        : bankAccountForCategoryAndState.data.spruceNickname;
      const displayName = selectedNickname ? `${baseName} ${selectedNickname}` : baseName;
      setFieldValue(`${fieldName}.${index}.destinationAccount.name`, displayName);
      setFieldValue(`${fieldName}.${index}.destinationAccount.id`, bankAccountForCategoryAndState.data.id);
      setFieldTouched(`${fieldName}.${index}.destinationAccount`, true);
    }
  }, [
    bankAccountForCategoryAndState,
    fieldName,
    index,
    setFieldTouched,
    setFieldValue,
    isManualDestinationAccountState,
    isSpruceWithZillowEnabled,
    isZillowClosingEnabled,
  ]);

  const category = (values as IEscrowFileCompleteData)[fieldName][index].category;

  useEffect(() => {
    if (!isManualDestinationAccountState && category !== undefined) {
      dispatch(
        getBankAccountForCategoryAndStateAction({
          category: category,
          usState: orderData.property.state ?? '',
          rowIndex: String(index),
        })
      );
    }
  }, [category, dispatch, index, isManualDestinationAccountState, orderData.property.state]);

  const destinationAccountOptions: ISelectOption[] = destinationAccounts.map((account) => {
    return {
      label: (() => {
        const base = isSpruceWithZillowEnabled ? account.legalName : account.spruceName;
        const selectedNickname = isZillowClosingEnabled ? account.nickname : account.spruceNickname;
        return selectedNickname ? `${base} ${selectedNickname}` : base;
      })(),
      value: account.id,
    };
  });

  return (
    <>
      <Grid>
        <Grid columnCount={4} alignItems={`center`}>
          <NumberField
            name={`${fieldName}.${index}.amountCents`}
            label="Amount"
            mode="currency"
            placeholder="123,456.78"
            valueIsCents
            disabled={!hasEditPermission}
          />
          <SelectField
            name={`${fieldName}.${index}.category`}
            options={legacyInternalTransferCategoriesToOptions(
              getFilteredLegacyInternalTransferCategories(parties),
              getDisabledLegacyInternalTransferCategories(state)
            ).toSorted(labelComparator)}
            label={'Category'}
            placeholder={'Select one...'}
            isDisabled={!hasEditPermission}
          />
          {isManualDestinationAccountState ? (
            <SelectField
              name={`${fieldName}.${index}.destinationAccount.id`}
              options={destinationAccountOptions}
              label={'Destination Account'}
              placeholder={'Select one...'}
            />
          ) : (
            <TextField name={`${fieldName}.${index}.destinationAccount.name`} label={'Destination Account'} readOnly />
          )}
          <FlexRow justifyContent={'start'} verticalAlign={'center'}>
            <Button intent={'ghost'} isInline onClick={() => onDelete(index)} disabled={!hasEditPermission}>
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </FlexRow>
          {transferData.createdAt && !fetching && (
            <FlexRow justifyContent={'start'} verticalAlign={'center'}>
              <Button intent={'primary'} onClick={onSend} disabled={!hasEditPermission}>
                Send
              </Button>
            </FlexRow>
          )}
        </Grid>
      </Grid>
    </>
  );
};
